import React, { useContext } from 'react';
import { ProSidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import 'react-pro-sidebar/dist/css/styles.css';
import { tokens } from '../../theme';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import AccountBalanceSharpIcon from '@mui/icons-material/AccountBalanceSharp';
import Inventory2SharpIcon from '@mui/icons-material/Inventory2Sharp';
import DescriptionIcon from '@mui/icons-material/Description';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import LanguageIcon from '@mui/icons-material/Language';
import Logo from '../../assets/images/logo.png';
import ConstructionIcon from '@mui/icons-material/Construction';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SummarizeIcon from '@mui/icons-material/Summarize';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import SearchIcon from '@mui/icons-material/Search';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import AssistantPhotoIcon from '@mui/icons-material/AssistantPhoto';
import KeyIcon from '@mui/icons-material/Key';
import { MenuAccessContext } from '../../utility/getRoles';

const menuConfig = {
  Settings: {
    icon: <SettingsIcon />,
    submenus: {
      Service: {
        path: '/settings/service',
        icon: <ConstructionIcon />,
        name: 'Service',
      },
      Manage: {
        path: '/settings/manage',
        icon: <ManageAccountsIcon />,
        name: 'Manage',
      },
      User: {
        path: '/settings/user',
        icon: <PersonIcon />,
        name: 'User',
      },
      RiskProfile: {
        path: '/settings/risk-profile',
        icon: <VerifiedUserIcon />,
        name: 'Risk Profile',
      },
      AdditionalQuestionnaire: {
        path: '/settings/risk-profile-questionnaire',
        icon: <VerifiedUserIcon />,
        name: 'Additional Questionnaire',
      },
      Strategy: {
        path: '/settings/strategy',
        icon: <AutoGraphIcon />,
        name: 'Strategy',
      },
      RoleMaster: {
        path: '/settings/rolemaster',
        icon: <AutoGraphIcon />,
        name: 'Role Master',
      },
      RoleMenuAccess: {
        path: '/settings/rolemenuaccess',
        icon: <AutoGraphIcon />,
        name: 'Role Menu Access',
      },
    },
  },
  Dashboard: {
    icon: <HomeOutlinedIcon />,
    path: '/dashboard',
    name: 'Dashboard',
  },
  Profile: {
    icon: <AccountCircleIcon />,
    path: '/profile',
    name: 'Profile',
  },
  Create: {
    icon: <DescriptionIcon />,
    submenus: {
      KRACreateStatus: {
        path: '/kra-create-status',
        icon: <NoteAddIcon />,
        name: 'KRA Create Status',
      },
      CKYCCreateStatus: {
        path: '/ckyc-create-status',
        icon: <NoteAddIcon />,
        name: 'CKYC Create Status',
      },
    },
  },
  Bank: {
    icon: <AccountBalanceSharpIcon />,
    path: '/bank',
    name: 'Bank',
  },
  InternetProfile: {
    icon: <LanguageIcon />,
    path: '/internet-profile',
    name: 'Internet Profile',
  },
  EmailKYC: {
    icon: <MailOutlineIcon />,
    path: '/email-kyc',
    name: 'Email KYC',
  },
  AssistedMode: {
    icon: <AssistantPhotoIcon />,
    path: '/assisted-mode',
    name: 'Assisted Mode',
  },
  Research: {
    icon: <TrendingUpIcon />,
    submenus: {
      'Research Services': {
        path: '/research/service',
        icon: <ConstructionIcon />,
        name: 'Research Service',
      },
      'Research Email': {
        path: '/research/email',
        icon: <MailOutlineIcon />,
        name: 'Research Email',
      },
    },
  },
  Reports: {
    icon: <SummarizeIcon />,
    path: '/reports',
    name: 'Reports',
  },
  ResearchClientData: {
    icon: <FormatAlignLeftIcon />,
    path: '/research/excel',
    name: 'Research Client Data',
  },
  CheckerReports: {
    icon: <SummarizeIcon />,
    path: '/checker-reports',
    name: 'Checker Reports',
  },
  CounterEsignReports: {
    path: '/countersign-reports',
    icon: <DescriptionIcon />,
    name: 'Counter Esign Reports',
  },
  TenantsReports: {
    icon: <AccountCircleIcon />,
    path: '/tenants-reports',
    name: 'Tenants Reports',
  },
  PaymentDetails: {
    icon: <LanguageIcon />,
    path: '/payment-details',
    name: 'Payment Details',
  },
  Encryption: {
    icon: <KeyIcon />,
    path: '/encryption',
    name: 'Encryption',
  },
  PanStatusSearch: {
    icon: <SearchIcon />,
    path: '/pan-status-search',
    name: 'PAN Status Search',
  },
  ChangePassword: {
    icon: <EnhancedEncryptionIcon />,
    path: '/change-password',
    name: 'Change Password',
  },
  DataPoint: {
    path: '/client-data',
    icon: <DataObjectIcon />,
    name: 'Client Data Point',
  },
  DataDeletePoint: {
    path: '/delete-data',
    icon: <DeleteIcon />,
    name: 'Client Data Cleanup',
  },
  KYCDataComparison: {
    path: '/kyc-data-comparison',
    icon: <CompareArrowsIcon />,
    name: 'KYC Data Comparison',
  },
  Estamp: {
    path: '/estamp',
    icon: <DescriptionIcon />,
    name: 'Estamp',
  },
  Products: {
    path: '/products',
    icon: <Inventory2SharpIcon />,
    name: 'Products',
  },
};

const Item = ({ title, to, icon, selected, setSelected }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleRenderSidebar = () => {
    navigate(to);
    setSelected(title);
  };
  return (
    <MenuItem
      active={selected === title}
      style={{ color: colors.grey[100] }}
      onClick={() => handleRenderSidebar()}
      icon={icon}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const Sidebar = ({ selected, setSelected, isCollapsed, setIsCollapsed }) => {
  const { menuAccess } = useContext(MenuAccessContext);
  const topLevelMenus = menuAccess
    ?.filter((menu) => !menu?.submenus || menu?.submenus?.length === 0)
    ?.filter((menu, index, self) => self.findIndex((m) => m.menuId === menu.menuId) === index);
  const subMenuGroups = menuAccess
    ?.filter(
      (menu) =>
        Array.isArray(menu?.submenus) &&
        menu?.submenus.length > 0 &&
        menu?.submenus.some((sub) => sub.submenuName),
    )
    ?.filter((menu, index, self) => self.findIndex((m) => m.menuId === menu.menuId) === index);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        overflowY: 'auto',
        zIndex: 1200,

        '& .pro-sidebar': { width: '260px', minWidth: '260px' },
        '& .pro-sidebar.collapsed': { width: '80px', minWidth: '80px' },
        '& .pro-sidebar.collapsed .menuHead': { margin: '5px 0 5px 20px' },
        '& .pro-sidebar.collapsed .pro-icon-wrapper': { marginLeft: '20px !important' },
        '& .pro-sidebar-inner': {
          background: `${colors.primary[400]} !important`,
          borderRight: `1px solid ${colors.grey[900]}`,
        },
        '& .pro-icon-wrapper': {
          backgroundColor: 'transparent !important',
          marginRight: '15px !important',
          marginLeft: '0px !important',
        },
        '& .pro-inner-item': { padding: '5px 10px 5px 0px !important' },
        '& .pro-inner-item img': {
          padding: '0 0px 0 20px',
          width: '55%',
          marginTop: '-10px',
        },
        '& .pro-inner-item:hover': { color: '#3c64d0 !important' },
        '& .pro-menu-item.active': { color: '#3c64d0 !important' },
        '& p.MuiTypography-root': { fontSize: '16px' },
        '& li.pro-menu-item.pro-sub-menu, span.pro-item-content, span.pro-icon': {
          color: `${colors.grey[100]} !important`,
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: '10px 0 20px 0',
              color: colors.grey[100],
            }}
          >
            {!isCollapsed && (
              <Box display="flex" justifyContent="space-between" alignItems="center" ml="15px">
                <img src={Logo} alt="logo" />
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          <Box paddingLeft={isCollapsed ? undefined : '10%'}>
            {topLevelMenus.map((menu) => {
              const config = menuConfig[menu.menuName];
              if (!config || !config.path) return null;

              return (
                <Item
                  key={`top-${menu.menuId}`}
                  title={config.name ?? menu.menuName}
                  to={config.path}
                  icon={config.icon}
                  selected={selected}
                  setSelected={setSelected}
                />
              );
            })}

            {subMenuGroups.map((menu) => {
              const config = menuConfig[menu.menuName];
              if (!config || !config.submenus) {
                return null;
              }

              return (
                <SubMenu key={menu.menuId} title={menu.menuName} icon={config.icon}>
                  {menu.submenus.map((submenu) => {
                    const submenuConfig = config.submenus?.[submenu.submenuName];
                    if (!submenuConfig) {
                      return null;
                    }
                    return (
                      <Item
                        key={submenu.submenuId}
                        title={submenuConfig?.name}
                        to={submenuConfig.path}
                        icon={submenuConfig.icon}
                        selected={selected}
                        setSelected={setSelected}
                      />
                    );
                  })}
                </SubMenu>
              );
            })}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
